import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Navigate } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import partnerApi, { getPartner, partnerLogout } from "../../lib/partnerApi";
import {
  LayoutGrid, BarChart3, Settings, KeyRound, FlaskConical,
  LogOut, ExternalLink,
} from "lucide-react";

const NAV = [
  { to: "/partner/dashboard", label: "Dashboard", icon: LayoutGrid },
  { to: "/partner/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/partner/settings", label: "Settings", icon: Settings },
  { to: "/partner/api-keys", label: "API Keys", icon: KeyRound },
  { to: "/partner/sandbox", label: "Sandbox", icon: FlaskConical },
];

export function PartnerLayout({ children, title, subtitle, actions }) {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(getPartner());
  useEffect(() => {
    partnerApi.get("/partner/me").then((r) => {
      setPartner(r.data);
      localStorage.setItem("xch_partner", JSON.stringify(r.data));
    }).catch(() => {
      partnerLogout(); navigate("/partner/login");
    });
  }, [navigate]);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-60 shrink-0 flex flex-col p-4 sticky top-0 h-screen bg-white border-r border-border" data-testid="partner-sidebar">
        <div className="flex items-center gap-2 mb-6 px-1">
          {partner?.logo_url ? (
            <img src={partner.logo_url} alt="logo" className="h-7 max-w-[150px] object-contain" />
          ) : <Brand size={24} />}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold px-2 mb-2">
          {partner?.name || "Partner"}
        </div>
        <nav className="space-y-0.5 flex-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to} to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isActive ? "bg-primary text-white" : "text-foreground/70 hover:text-foreground hover:bg-muted"
                }`
              }
              data-testid={`partner-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border pt-3 space-y-1">
          <button onClick={() => navigate("/")}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  data-testid="partner-back">
            <ExternalLink className="w-3.5 h-3.5" /> Back to app
          </button>
          <button onClick={() => { partnerLogout(); navigate("/partner/login"); }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  data-testid="partner-logout">
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 px-8 py-8" data-testid="partner-content">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h1 className="xch-headline text-3xl">{title}</h1>
            {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          {actions}
        </div>
        {children}
      </main>
    </div>
  );
}

export function PartnerGuard({ children }) {
  const token = typeof window !== "undefined" && localStorage.getItem("xch_partner_token");
  if (!token) return <Navigate to="/partner/login" replace />;
  return children;
}
